import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';

import { useGetPolicy } from '@services/policy';
import { GridLink } from '@components/gridLink/GridLink';
import { addSpaces } from '@utils/addSpaces';
import { Table } from '@components/table/Table';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { PolicyGetResponse } from '@lio/api-types';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';

const associatedPoliciesColumns: TableGridBaseColDef[] = [
  {
    field: 'policyNumber',
    headerName: 'Policy Number',
    flex: 1,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams) => (
      <OverflowTooltip>
        <GridLink
          to={`/policy/${params.row.policyData.id}`}
        >{`${params.row?.policyData?.policyNumberPrefix}${params?.row?.policyData?.policyNumber?.policyNumber}`}</GridLink>
      </OverflowTooltip>
    ),
  },
  {
    field: 'lineOfBusiness',
    headerName: 'Line of Business',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => row?.policyData.packageLabel ?? '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    sortable: false,
    valueGetter: (value, row) => row?.policyData.status ?? '-',
  },
  {
    field: 'policyType',
    headerName: 'Relationship',
    flex: 2,
    sortable: false,
    valueGetter: (value, row) => row?.policyRelationType ?? '-',
  },
];

export const AssociatedPoliciesTab = () => {
  const { policyId } = useParams();

  const formatData = useCallback((policy: PolicyGetResponse) => {
    const relatedPoliciesData = [...(policy?.relatedPolicyDestinations ?? []), ...(policy?.relatedPolicySources ?? [])];
    const relatedPolicies = relatedPoliciesData.map((el) => {
      let policyData = { policyRelationType: '' };
      const relationshipType = addSpaces(el.policyRelationType);
      if (Object.keys(el).includes('destinationPolicy')) {
        policyData = { ...(el as any).destinationPolicy, policyRelationType: `${relationshipType} Destination` };
      } else if (Object.keys(el).includes('sourcePolicy')) {
        policyData = { ...(el as any).sourcePolicy, policyRelationType: `${relationshipType} Source` };
      }
      return { id: el.id, policyRelationType: policyData.policyRelationType, policyData };
    });
    return relatedPolicies;
  }, []);

  return (
    <Table
      ariaLabel='associated-policies-tab-grid'
      columns={associatedPoliciesColumns}
      onLoadData={useGetPolicy as any}
      dataFormatter={formatData}
      noResultsConfig={{ noResultsMessage: 'No Associated Policies found' }}
      // isInitiating={isPolicyTermsLoading}
      filterConfig={{
        defaultFilters: {
          policyId,
        },
        filterQueryParam: {
          policyId: 'policyId',
        },
      }}
      sxProps={{
        tableContainer: {
          ...styles.dataGridContainer,
          display: 'block',
          height: 'fit-content',
        },
        dataGridContainer: {
          ...styles.dataGridContainer,
          borderLeft: 'none',
          padding: 0,
          height: 'auto',
        },
        dataGrid: styles.dataGrid,
      }}
    />
  );
};
